// @flow

import * as React from 'react';
import { ShareDropdownMenu, ShareTarget, ShareTracker } from '@ta/public.share-cta';
import Popover from '@ta/overlays.popover';
import { Mutation } from 'react-apollo';
import type { MutationFunction } from 'react-apollo';
import { LoadingSpinner } from '@ta/styleguide.loading';
import WindowScroll from '@ta/events.window-scroll';
import { unescaped, localize } from '@ta/common.i18n';
import { InteractionLogger } from '@ta/tracking.interactions';
import { isOwner, buildPublicTripShareUrl } from '@ta/trips.trip-util';
import * as Types from '@ta/trips.trip-types';
import type { ExternalRepostProps } from '@ta/social.types';

import mutation from './CreateTripToken.gql';
import type {
  CreateTripToken as Data,
  CreateTripTokenVariables as Variables,
} from './CreateTripToken.gql';
import styles from './ShareTripPopup.less';

/** READ will create READ tokens. WRITE will create WRITE tokens and PUBLIC will use the canonical url */
type ShareType = 'READ' | 'WRITE' | 'PUBLIC';

type ShareTripProps = {|
  trip: Types.Trip,
  shareType: ShareType,
  children: (
    target: (?HTMLElement) => void,
    open: () => void,
    string,
    close: () => void,
  ) => React.Node,
  alignEdge: 'right' | 'left',
  shiftArrow: number,
  closeOnScroll?: boolean,
  /** Optional repost props */
  repostProps?: ExternalRepostProps,
|};

type ShareTripActionProps = {|
  trip: Types.Trip,
  shareType: ShareType,
  alignEdge: 'right' | 'left',
  onClick?: () => void,
  /** Optional repost props */
  repostProps?: ExternalRepostProps,
|};

type ShareTripState = {|
  shareUrl: ?string,
  shareUrlType: ShareType,
|};

const HEADER_TEXT = {
  READ: () => unescaped(localize('trips_share_read_only', { className: styles.strong })), // Invite your friends to <span class='{className}'>view your Trip</span>. Send an invite via:
  WRITE: () => unescaped(localize('trips_share_read_write', { className: styles.strong })), // Invite friends to <span class='{className}'>edit your Trip</span>. Send an invite via:
  PUBLIC: () => unescaped(localize('trips_share_read_only', { className: styles.strong })), // Invite your friends to <span class='{className}'>view your Trip</span>. Send an invite via:
};

/**
 * Popup for sharing trips with potential collaborators or publicly. Delegates most of the work to the share menu.
 * It caches one token per session so we don't overwhelm the save service with token requests
 * if the user opens and closes the menu rapidly.
 *
 * If the shareType is public, use the canonical url instead of a token url.
 */
class ShareTripPopup extends React.Component<ShareTripProps, ShareTripState> {
  static defaultProps = {
    alignEdge: 'left',
    shiftArrow: 0,
    closeOnScroll: false,
  };

  constructor(props: ShareTripProps) {
    super(props);

    const tripUrl =
      (props.trip.route && props.trip.route.url) || buildPublicTripShareUrl(props.trip.id);
    const shareUrl = props.shareType === 'PUBLIC' ? tripUrl : '';
    this.state = { shareUrl, shareUrlType: props.shareType };
  }

  onTargetClick(createToken: MutationFunction<Data, Variables>, openPopup: () => void) {
    // always open the popup, only do the mutation if we have no token or the shareType has changed
    if (!this.state.shareUrl || this.state.shareUrlType !== this.props.shareType) {
      this.updateShareUrl(createToken);
    }

    openPopup();
  }

  updateShareUrl(createToken: MutationFunction<Data, Variables>) {
    if (this.props.shareType === 'PUBLIC') {
      this.setState({
        shareUrl: buildPublicTripShareUrl(this.props.trip.id),
        shareUrlType: 'PUBLIC',
      });
      return;
    }

    const variables = { tripId: this.props.trip.id, type: this.props.shareType };
    createToken({ variables }).then(response => {
      // we use the relative url so that the sharing logic will use the given hostname
      this.setState({
        shareUrl: response?.data?.createTripToken?.route?.url,
        shareUrlType: this.props.shareType,
      });
    });
  }

  renderContent() {
    const { shareUrl } = this.state;
    const { trip } = this.props;
    const { repostProps } = this.props;

    let headerText = HEADER_TEXT[this.props.shareType]();
    let tripType = this.props.shareType === 'WRITE' ? 'WriteTrip' : 'ReadTrip';
    if (!isOwner(trip)) {
      // Not owner or collaborator
      if (trip && trip.actionPermissions) {
        if (!trip.actionPermissions.canEdit) {
          headerText = null;
          tripType = 'NotMyTrip';
        }
      } else {
        headerText = null;
        tripType = 'NotMyTrip';
      }
    }

    return shareUrl ? (
      <React.Fragment>
        {headerText && <div className={styles.header}>{headerText}</div>}
        <ShareDropdownMenu
          url={shareUrl}
          itemType={tripType}
          itemId={`${trip.id}`}
          repostProps={repostProps}
        />
      </React.Fragment>
    ) : (
      <LoadingSpinner size="small" />
    );
  }

  render() {
    const { closeOnScroll } = this.props;

    return (
      <Popover
        local
        closeButton={false}
        below
        alignEdge={this.props.alignEdge}
        arrowType="small"
        shiftArrow={this.props.shiftArrow}
        contents={() => this.renderContent()}
      >
        {(target, openPopup, closePopup) => (
          <React.Fragment>
            {closeOnScroll && <WindowScroll callback={closePopup} throttle={500} />}
            <Mutation mutation={mutation}>
              {CreateTripToken =>
                this.props.children(
                  target,
                  () => this.onTargetClick(CreateTripToken, openPopup),
                  // $FlowFixMe I need an adult to handle null here please
                  this.state.shareUrl,
                  closePopup,
                )
              }
            </Mutation>
          </React.Fragment>
        )}
      </Popover>
    );
  }
}

/**
 * Share Trip popup with default social stylings
 */
const ShareTripPopupAction = ({
  trip,
  shareType,
  alignEdge,
  onClick,
  repostProps,
}: ShareTripActionProps) => (
  <ShareTripPopup trip={trip} shareType={shareType} alignEdge={alignEdge} repostProps={repostProps}>
    {(target, openPopup, shareUrl) => (
      <ShareTracker
        itemType="Trip"
        itemId={`${trip.id}`}
        url={shareUrl}
        action="share_button_click"
      >
        {() => (
          <InteractionLogger>
            {logInteraction => (
              <ShareTarget
                onClick={e => {
                  if (onClick) {
                    onClick();
                  }
                  logInteraction(e, 'share');
                  openPopup();
                }}
                target={target}
                styleAsSocialAction
              />
            )}
          </InteractionLogger>
        )}
      </ShareTracker>
    )}
  </ShareTripPopup>
);

ShareTripPopupAction.defaultProps = { onClick: undefined };

export { ShareTripPopup, ShareTripPopupAction };
